import { countries, currentMsMarket } from "@ms/us/src/@ms/gatsby-site/config/navigation";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useMemo } from "react";

import { getLocalizedNavData } from "@ms/gatsby-theme";

import type {
  NavigationDataProps,
  NavigationStaticText,
  SiteNavData,
  SiteNavQueryData,
} from "@ms/gatsby-theme";

const baseUrl =
  process.env.MS_ENVIRONMENT === "staging"
    ? "https://stg.us.manscaped.io"
    : "https://www.manscaped.com";

const NAVIGATION_QUERY = graphql`
  query NavigationQuery {
    allStrapiSite(filter: { Name: { ne: "__DO_NOT_REMOVE" } }) {
      nodes {
        Instance
        navigation {
          localizations {
            locale
          }
          id
          locale
          categories {
            ...StrapiNavigationCategory
          }
          dropdownLinks {
            ...StrapiDropdownLinks
          }
        }
      }
    }
    shopAllCategory: strapiCategory(slug: { eq: "shop-all" }, locale: { eq: "en" }) {
      ...StrapiNavigationCategory
    }
    strapiBlogAsset {
      featuredImage {
        title
        subtitle
        cta {
          label
          link
          ariaLabel
        }
        image {
          cloudinaryMedia {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
  }
`;

/**
 * Use Nav Data
 *
 * This hook takes in the Navigation from Strapi for the given instance and localizes the content -
 * also to allow all navigation components to live in our gatsby-theme component library so we can use
 * the same nav in multiple places
 *
 * @returns {NavigationDataProps} navData
 */
export const useBlogNavData = (): NavigationDataProps => {
  const { allStrapiSite, shopAllCategory, strapiBlogAsset } = useStaticQuery(NAVIGATION_QUERY);

  const { t } = useTranslation("navigation");

  const siteNavData: SiteNavQueryData = allStrapiSite.nodes.find(
    (site: { Instance: string }) => site.Instance.toUpperCase() === "US",
  )?.navigation;

  const currentLanguage = "en";

  const navigationStaticText: NavigationStaticText = useMemo(
    () => ({
      shopAll: "Shop All",
      shopNow: "Shop Now",
      back: "Back",
    }),
    [],
  );

  const localizedNavData: SiteNavData = useMemo(
    () =>
      getLocalizedNavData(
        siteNavData,
        shopAllCategory,
        currentLanguage,
        currentMsMarket,
        true,
        navigationStaticText,
        strapiBlogAsset,
      ),
    [shopAllCategory, siteNavData, strapiBlogAsset, navigationStaticText],
  );

  // Filter dropdown links to only show PHP link when isPHPEligible is true
  const filteredDropdownLinks = useMemo(() => {
    if (!localizedNavData.dropdownLinks) return [];

    return localizedNavData.dropdownLinks.filter((link) => {
      const hasPhpLink = link.links.some((linkItem) => linkItem.url === "/pages/peak-hygiene-plan");

      return !hasPhpLink;
    });
  }, [localizedNavData.dropdownLinks]);

  return {
    cart: {
      itemsInCart: 0,
      to: `${baseUrl}/cart`,
    },
    isAuthenticated: false,
    isBlog: true,
    countries: countries,
    currentMsMarket: currentMsMarket,
    languages: [currentLanguage],
    currentLanguage: currentLanguage,
    originalPath: "",
    siteNavData: localizedNavData,
    dropdownLinks: filteredDropdownLinks,
    blogLink: null,
    homeUrl: `${baseUrl}/`,
    accountAuthenticatedLink: {
      to: `${baseUrl}/account/edit`,
      label: t("navigationMyAccount", { defaultValue: "My Account" }),
      mobileLabel: t("navigationMyAccount", { defaultValue: "My Account" }),
    },
    productLabel: t("navigationProducts", { defaultValue: "Products" }),
    accountLabel: t("navigationMyAccount", { defaultValue: "My Account" }),
    loginLink: {
      to: `${baseUrl}/login`,
      label: t("navigationSignIn", { defaultValue: "Sign In" }),
      mobileLabel: t("navigationLoginMyAccount", { defaultValue: "Log in to My Account" }),
    },
    signOut: { action: () => null, label: t("navigationSignOut", { defaultValue: "Sign Out" }) },
    backCTAText: navigationStaticText.back,
    shopAllCTAText: navigationStaticText.shopAll,
    shopNowCTAText: navigationStaticText.shopNow,
  };
};
